import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, RotateCcw, Search } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const roleColor = { admin: "bg-violet-100 text-violet-700", accountant: "bg-blue-100 text-blue-700", staff: "bg-emerald-100 text-emerald-700" };

export default function DeletedStaff() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [permTarget, setPermTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/staff/deleted").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (s) => {
    if (!window.confirm(`Restore ${s.name} back to active staff?`)) return;
    try { await api.post(`/staff/${s.id}/restore`); toast.success("Staff restored"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const permanentDelete = async () => {
    if (!permTarget) return;
    try {
      await api.delete(`/staff/${permTarget.id}/permanent`);
      toast.success("Staff permanently deleted"); setPermTarget(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const q = search.toLowerCase().trim();
  const filtered = rows.filter((s) => !q || [s.name, s.username, s.phone, s.designation].some((v) => (v || "").toLowerCase().includes(q)));

  const canManage = hasPerm(user, "manage_users");
  const dash = <span className="text-slate-300">—</span>;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Trash2 className="text-rose-500" size={26} />Deleted Staff</h1>
        <p className="text-slate-500">{rows.length} removed staff member{rows.length !== 1 ? "s" : ""} · restore any record anytime</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search name, username, phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="deleted-staff-search" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400"><Trash2 size={40} className="mx-auto mb-2 opacity-40" />No deleted staff.</div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-300px)]">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 text-left border border-slate-200">Staff</th>
                    <th className="px-3 py-2.5 text-left border border-slate-200">Username</th>
                    <th className="px-3 py-2.5 text-left border border-slate-200">Role</th>
                    <th className="px-3 py-2.5 text-left border border-slate-200">Access</th>
                    <th className="px-3 py-2.5 text-left border border-slate-200">Phone</th>
                    <th className="px-3 py-2.5 text-left border border-slate-200">Removed On</th>
                    <th className="px-3 py-2.5 text-left border border-slate-200">Removed By</th>
                    <th className="px-3 py-2.5 text-center border border-slate-200">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`deleted-staff-row-${s.username}`}>
                      <td className="px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center gap-2.5">
                          <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 overflow-hidden flex items-center justify-center text-xs font-semibold shrink-0 border border-slate-200">
                            {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : initials(s.name)}
                          </div>
                          <div className="font-medium text-slate-800">{s.name}</div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-500">{s.username || dash}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{s.designation || dash}</td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleColor[s.role] || "bg-slate-100 text-slate-600"}`}>{s.role}</span>
                      </td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{s.phone || dash}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-500">{s.deleted_at ? s.deleted_at.slice(0, 10) : dash}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-500">{s.deleted_by || dash}</td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center justify-center gap-1.5">
                          {canManage && (
                            <Button size="sm" variant="outline" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => restore(s)} data-testid={`restore-staff-${s.username}`}>
                              <RotateCcw size={14} />Restore
                            </Button>
                          )}
                          {canManage && (
                            <Button size="sm" variant="outline" className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setPermTarget(s)} data-testid={`perm-delete-staff-${s.username}`}>
                              <Trash2 size={14} />Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <AlertDialog open={!!permTarget} onOpenChange={(o) => !o && setPermTarget(null)}>
        <AlertDialogContent data-testid="perm-delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-600">Permanently Delete Staff?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{permTarget?.name}</b> ({permTarget?.username}). This action <b>cannot be undone</b> and the staff member cannot be restored afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="perm-delete-staff-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={permanentDelete} data-testid="perm-delete-staff-confirm">Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
